import AbstractComponent from "./abstract-component";
import Swiper from "swiper";
import { Navigation, EffectFade } from 'swiper/modules';

Swiper.use([Navigation, EffectFade]);

export default class Realisations extends AbstractComponent {
    constructor(el) {
        super(el);
        this.DOM = {};
        this.items = [];
        this.realisationsData = [];
        this.modal = null;
        this.currentRealisationIndex = -1;
        this.swiper = null;
        this.isClosing = false;
        this.timeline = null;
        this.mediaPlayers = [];
        this.posterCache = new Map();
        this.posterPending = new Map();
        this.posterObserver = null;
        this.posterExtractor = null;
        this.initDomElements();
        this.initEvents();
        this.initPosterExtractor();
        this.initCardPosterExtraction();
    }

    initDomElements() {
        this.DOM.items = this.$el.querySelectorAll('.c-realisations__item');
        this.items = Array.from(this.DOM.items);

        this.items.forEach((item, index) => {
            const dataScript = item.querySelector('.c-realisations__item__data');
            if (dataScript) {
                try {
                    const data = JSON.parse(dataScript.textContent);
                    this.realisationsData.push({
                        id: data.id,
                        url: data.url,
                        title: data.title || '',
                        location: data.location || '',
                        gallery: data.gallery || [],
                        card_video: data.card_video || null,
                    });
                } catch (e) {
                    console.error('Error parsing realisation data:', e, dataScript.textContent);
                }
            } else {
                console.warn(`No data script found for item ${index}`);
            }
        });

        this.realisationsData.forEach((realisation) => {
            if (realisation.card_video && realisation.card_video.poster && realisation.card_video.src) {
                this.posterCache.set(realisation.card_video.src, realisation.card_video.poster);
            }

            (realisation.gallery || []).forEach((media) => {
                if (media.type === 'video' && media.poster && media.src) {
                    this.posterCache.set(media.src, media.poster);
                }
            });
        });

        this.initModal();
    }

    initModal() {
        this.modal = document.querySelector('.c-realisation-modal');

        if (!this.modal) {
            this.createModal();
        } else {
            this.initModalElements();
        }

        this.checkUrlForModal();
    }

    createModal() {
        console.error('Modal should be in the Twig template, not created in JS');
    }

    initModalElements() {
        if (!this.modal) {
            console.error('Cannot init modal elements: modal not found');
            return;
        }

        this.DOM.modal = {
            overlay: this.modal.querySelector('.c-realisation-modal__overlay'),
            close: this.modal.querySelector('.c-realisation-modal__close'),
            container: this.modal.querySelector('.c-realisation-modal__container'),
            slider: this.modal.querySelector('.c-realisation-modal__slider'),
            sliderWrapper: this.modal.querySelector('.swiper-wrapper'),
            prev: this.modal.querySelector('.c-realisation-modal__nav--prev'),
            next: this.modal.querySelector('.c-realisation-modal__nav--next'),
            title: this.modal.querySelector('.c-realisation-modal__title'),
            location: this.modal.querySelector('.c-realisation-modal__location'),
            caption: this.modal.querySelector('.c-realisation-modal__caption'),
            counter: this.modal.querySelector('.c-realisation-modal__counter'),
        };

        if (this.DOM.modal.close) {
            this.DOM.modal.close.addEventListener('click', () => this.closeModal());
        }
        if (this.DOM.modal.overlay) {
            this.DOM.modal.overlay.addEventListener('click', () => this.closeModal());
        }

        this.initSwiper();

        this.keyboardHandler = (e) => this.handleKeyboard(e);
        document.addEventListener('keydown', this.keyboardHandler);

        window.addEventListener('popstate', () => this.handlePopState());
    }

    initEvents() {
        if (!this.DOM.items || this.DOM.items.length === 0) {
            return;
        }

        this.items.forEach((item, index) => {
            item.style.cursor = 'pointer';

            if (item.classList.contains('c-realisations__item--has-video')) {
                item.addEventListener('mouseenter', () => this.playCardVideo(item));
                item.addEventListener('mouseleave', () => this.pauseCardVideo(item));
            }

            item.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.pauseCardVideo(item);
                this.updateUrlWithRealisation(index);
                this.openModal(index);
            });
        });
    }

    getCardVideo(item) {
        return item ? item.querySelector('.c-realisations__item__video') : null;
    }

    getVideoSrc(video) {
        if (!video) return '';

        const source = video.querySelector('source');

        return video.getAttribute('data-src')
            || video.getAttribute('src')
            || (source ? source.getAttribute('src') : '')
            || video.currentSrc
            || '';
    }

    isModalOpen() {
        return !!(this.modal && this.modal.classList.contains('is-open'));
    }

    initPosterExtractor() {
        this.posterExtractor = document.createElement('video');
        this.posterExtractor.muted = true;
        this.posterExtractor.playsInline = true;
        this.posterExtractor.setAttribute('playsinline', '');
        this.posterExtractor.setAttribute('preload', 'auto');
        this.posterExtractor.style.cssText = 'position:fixed;width:0;height:0;opacity:0;pointer-events:none;left:-9999px;top:0';
        document.body.appendChild(this.posterExtractor);
    }

    isItemInViewport(item) {
        const rect = item.getBoundingClientRect();
        return rect.top < window.innerHeight + 200 && rect.bottom > -200;
    }

    needsAutoPoster(item) {
        const video = this.getCardVideo(item);
        if (!video || video.dataset.posterReady === 'true') return false;

        const image = item.querySelector('.c-realisations__item__image');
        return !(image && image.getAttribute('src'));
    }

    extractVideoFrameFromSrc(src) {
        if (!src) {
            return Promise.reject(new Error('Missing video source'));
        }

        if (this.posterCache.has(src)) {
            return Promise.resolve(this.posterCache.get(src));
        }

        if (this.posterPending.has(src)) {
            return this.posterPending.get(src);
        }

        const promise = new Promise((resolve, reject) => {
            const video = this.posterExtractor;
            if (!video) {
                reject(new Error('Poster extractor unavailable'));
                return;
            }

            const cleanup = () => {
                video.removeEventListener('loadeddata', onLoadedData);
                video.removeEventListener('error', onError);
            };

            const resetExtractor = () => {
                video.pause();
                video.removeAttribute('src');
                video.load();
            };

            const capture = () => {
                try {
                    const width = video.videoWidth;
                    const height = video.videoHeight;

                    if (!width || !height) {
                        throw new Error('Missing video dimensions');
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(video, 0, 0, width, height);

                    const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
                    this.posterCache.set(src, dataUrl);

                    cleanup();
                    resetExtractor();
                    resolve(dataUrl);
                } catch (error) {
                    cleanup();
                    resetExtractor();
                    reject(error);
                }
            };

            const onLoadedData = () => capture();

            const onError = () => {
                cleanup();
                resetExtractor();
                reject(new Error('Video load failed'));
            };

            resetExtractor();
            video.addEventListener('loadeddata', onLoadedData, { once: true });
            video.addEventListener('error', onError, { once: true });
            video.src = src;
            video.load();
        });

        this.posterPending.set(src, promise);
        promise.finally(() => this.posterPending.delete(src));

        return promise;
    }

    extractVideoFrame(video) {
        return this.extractVideoFrameFromSrc(this.getVideoSrc(video));
    }

    applyCardPoster(item, posterUrl) {
        const media = item.querySelector('.c-realisations__item__media');
        const video = this.getCardVideo(item);

        if (!media || !video || !posterUrl) return;

        let image = item.querySelector('.c-realisations__item__image');

        if (!image) {
            image = document.createElement('img');
            image.className = 'c-realisations__item__image';
            image.setAttribute('aria-hidden', 'true');
            image.alt = '';
            media.insertBefore(image, video);
        }

        image.src = posterUrl;
        video.setAttribute('poster', posterUrl);
        video.dataset.posterReady = 'true';
        item.classList.add('is-poster-ready');
    }

    applyVideoPoster(video, posterUrl) {
        if (!video || !posterUrl) return;
        video.setAttribute('poster', posterUrl);
        video.dataset.posterReady = 'true';
    }

    async generateCardPoster(item) {
        if (!this.needsAutoPoster(item)) return;

        const video = this.getCardVideo(item);
        const src = this.getVideoSrc(video);
        if (!video || !src) return;

        item.classList.add('is-poster-loading');

        try {
            const posterUrl = await this.extractVideoFrameFromSrc(src);
            this.applyCardPoster(item, posterUrl);
        } catch (error) {
            console.warn('Could not extract card video poster:', error);
        } finally {
            item.classList.remove('is-poster-loading');
        }
    }

    queueCardPoster(item) {
        if (item.dataset.posterQueued === 'true') return;
        item.dataset.posterQueued = 'true';
        this.generateCardPoster(item);
    }

    initCardPosterExtraction() {
        const items = this.items.filter((item) => this.needsAutoPoster(item));
        if (!items.length) return;

        const visible = [];
        const deferred = [];

        items.forEach((item) => {
            if (this.isItemInViewport(item)) {
                visible.push(item);
            } else {
                deferred.push(item);
            }
        });

        visible.forEach((item) => this.queueCardPoster(item));

        if (!deferred.length) return;

        if (this.posterObserver) {
            this.posterObserver.disconnect();
        }

        this.posterObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                this.posterObserver.unobserve(entry.target);
                this.queueCardPoster(entry.target);
            });
        }, { rootMargin: '500px 0px' });

        deferred.forEach((item) => this.posterObserver.observe(item));
    }

    async ensureModalVideoPoster(video) {
        if (!video || video.dataset.posterReady === 'true') return;

        const src = this.getVideoSrc(video);
        if (!src) return;

        try {
            const posterUrl = await this.extractVideoFrameFromSrc(src);
            this.applyVideoPoster(video, posterUrl);
        } catch (error) {
            console.warn('Could not extract modal video poster:', error);
        }
    }

    playCardVideo(item) {
        if (!item || this.isModalOpen()) return;

        const video = this.getCardVideo(item);
        if (!video) return;

        if (item.classList.contains('is-poster-loading')) return;

        if (this.needsAutoPoster(item)) {
            this.generateCardPoster(item).then(() => {
                if (item.matches(':hover')) {
                    this.playCardVideo(item);
                }
            });
            return;
        }

        const dataSrc = video.getAttribute('data-src');
        if (dataSrc && !video.getAttribute('src')) {
            video.setAttribute('src', dataSrc);
            video.load();
        }

        video.muted = true;

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
        }

        item.classList.add('is-video-playing');
    }

    pauseCardVideo(item, reset = false) {
        if (!item) return;

        const video = this.getCardVideo(item);
        if (!video) return;

        video.pause();

        if (reset) {
            video.currentTime = 0;
        }

        item.classList.remove('is-video-playing');
    }

    pauseAllCardVideos(reset = false) {
        this.items.forEach((item) => this.pauseCardVideo(item, reset));
    }

    resumeHoveredCardVideos() {
        if (this.isModalOpen()) return;

        this.items.forEach((item) => {
            if (item.matches(':hover')) {
                this.playCardVideo(item);
            }
        });
    }

    openModal(realisationIndex) {
        if (realisationIndex < 0 || realisationIndex >= this.realisationsData.length) {
            console.error('Invalid realisation index:', realisationIndex);
            return;
        }

        if (this.isClosing) return;

        this.currentRealisationIndex = realisationIndex;
        if (!this.modal) {
            console.error('Modal not found!');
            return;
        }

        this.isClosing = false;

        this.pauseAllCardVideos();
        this.updateModalContent();
        this.animateOpen();
    }

    closeModal() {
        if (!this.modal || this.isClosing) return;

        this.removeRealisationFromUrl();
        this.pauseAllCardVideos();
        this.pauseAllMedia();
        this.resetAllMedia();
        this.mediaPlayers = [];

        this.isClosing = true;
        this.animateClose(() => {
            if (this.DOM.modal.sliderWrapper) {
                this.DOM.modal.sliderWrapper.innerHTML = '';
            }
            this.isClosing = false;
            this.resumeHoveredCardVideos();
        });
    }

    initSwiper() {
        if (!this.DOM.modal.slider) {
            console.error('Swiper container not found');
            return;
        }

        this.swiper = new Swiper(this.DOM.modal.slider, {
            speed: 600,
            allowTouchMove: true,
            slidesPerView: 1,
            spaceBetween: 0,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            navigation: {
                nextEl: this.DOM.modal.next,
                prevEl: this.DOM.modal.prev,
            },
            on: {
                slideChange: () => {
                    this.pauseAllMedia();
                    this.prepareActiveMedia();
                    this.updateCaptionAndCounter();
                },
            }
        });
    }

    isVideoMedia(media) {
        return media && media.type === 'video';
    }

    escapeHtml(value) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    buildSlideContent(media) {
        if (this.isVideoMedia(media)) {
            const poster = media.poster ? ` poster="${this.escapeHtml(media.poster)}" data-poster-ready="true"` : '';
            const sourceType = media.mime ? ` type="${this.escapeHtml(media.mime)}"` : '';

            return `
                <div class="c-realisation-modal__video-wrapper">
                    <video
                        class="c-realisation-modal__video"
                        controls
                        playsinline
                        preload="metadata"${poster}
                    >
                        <source src="${this.escapeHtml(media.src)}"${sourceType}>
                    </video>
                </div>
            `;
        }

        return `
            <div class="c-realisation-modal__image-wrapper">
                <img class="c-realisation-modal__image" src="${this.escapeHtml(media.src)}" alt="${this.escapeHtml(media.alt)}" loading="lazy">
            </div>
        `;
    }

    getModalVideos() {
        if (!this.DOM.modal.sliderWrapper) {
            return [];
        }

        return Array.from(this.DOM.modal.sliderWrapper.querySelectorAll('.c-realisation-modal__video'));
    }

    pauseAllMedia() {
        this.getModalVideos().forEach((video) => {
            if (!video.paused) {
                video.pause();
            }
        });
    }

    resetAllMedia() {
        this.getModalVideos().forEach((video) => {
            video.pause();
            video.currentTime = 0;
        });
    }

    getActiveSlideVideo() {
        if (!this.swiper) return null;

        const activeSlide = this.swiper.slides[this.swiper.activeIndex];
        if (!activeSlide) return null;

        return activeSlide.querySelector('.c-realisation-modal__video');
    }

    prepareActiveMedia() {
        const activeVideo = this.getActiveSlideVideo();
        if (!activeVideo) return;

        this.ensureModalVideoPoster(activeVideo);
    }

    updateModalContent() {
        if (this.currentRealisationIndex < 0 || this.currentRealisationIndex >= this.realisationsData.length) {
            console.error('Invalid realisation index in updateModalContent');
            return;
        }

        const realisation = this.realisationsData[this.currentRealisationIndex];
        const gallery = realisation.gallery || [];

        if (gallery.length === 0) {
            console.warn('No gallery media, closing modal');
            this.closeModal();
            return;
        }

        this.pauseAllMedia();
        this.resetAllMedia();
        this.mediaPlayers = [];

        if (this.DOM.modal.sliderWrapper) {
            this.DOM.modal.sliderWrapper.innerHTML = '';
        }

        gallery.forEach((media) => {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            slide.innerHTML = this.buildSlideContent(media);
            this.DOM.modal.sliderWrapper.appendChild(slide);
        });

        this.mediaPlayers = this.getModalVideos();
        this.mediaPlayers.forEach((video, index) => {
            if (index === 0) {
                this.ensureModalVideoPoster(video);
            } else {
                window.setTimeout(() => this.ensureModalVideoPoster(video), index * 120);
            }
        });

        if (this.swiper) {
            setTimeout(() => {
                this.swiper.update();
                this.swiper.slideTo(0, 0);
                this.updateHeader(realisation);
                this.updateCaptionAndCounter();
                this.prepareActiveMedia();
            }, 50);
        }
    }

    updateHeader(realisation) {
        if (this.DOM.modal.title) {
            this.DOM.modal.title.textContent = realisation.title || '';
        }
        if (this.DOM.modal.location) {
            this.DOM.modal.location.textContent = realisation.location || '';
            this.DOM.modal.location.style.display = realisation.location ? '' : 'none';
        }
    }

    updateCaptionAndCounter() {
        if (!this.swiper || !this.realisationsData[this.currentRealisationIndex]) return;

        const realisation = this.realisationsData[this.currentRealisationIndex];
        const gallery = realisation.gallery || [];
        const index = this.swiper.realIndex || 0;
        const media = gallery[index] || null;

        if (this.DOM.modal.caption) {
            const text = media && (media.description || media.caption) ? (media.description || media.caption) : '';
            this.DOM.modal.caption.textContent = text || ' ';
        }

        if (this.DOM.modal.counter) {
            if (gallery.length > 1) {
                this.DOM.modal.counter.textContent = `${index + 1} / ${gallery.length}`;
                this.DOM.modal.counter.style.display = '';
            } else {
                this.DOM.modal.counter.textContent = '';
                this.DOM.modal.counter.style.display = 'none';
            }
        }

        if (this.DOM.modal.prev && this.DOM.modal.next) {
            const disabled = gallery.length <= 1;
            this.DOM.modal.prev.disabled = disabled;
            this.DOM.modal.next.disabled = disabled;
        }
    }

    animateOpen() {
        if (!this.modal || !this.DOM.modal.container) return;

        if (this.timeline) {
            this.timeline.kill();
        }

        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        this.timeline = this.app.gsap.timeline({
            onComplete: () => this.prepareActiveMedia(),
        });
        this.timeline
            .fromTo(this.modal, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.25, ease: 'power2.out' })
            .fromTo(this.DOM.modal.container, { autoAlpha: 0, scale: 0.96 }, { autoAlpha: 1, scale: 1, duration: 0.35, ease: 'power3.out' }, '-=0.1');
    }

    animateClose(onComplete) {
        if (!this.modal || !this.DOM.modal.container) {
            if (onComplete) onComplete();
            return;
        }

        if (this.timeline) {
            this.timeline.kill();
        }

        this.timeline = this.app.gsap.timeline({
            onComplete: () => {
                this.modal.classList.remove('is-open');
                this.modal.setAttribute('aria-hidden', 'true');
                document.body.style.overflow = '';
                if (onComplete) onComplete();
            }
        });

        this.timeline
            .to(this.DOM.modal.container, { autoAlpha: 0, scale: 0.96, duration: 0.25, ease: 'power2.in' })
            .to(this.modal, { autoAlpha: 0, duration: 0.25, ease: 'power2.in' }, '-=0.15');
    }

    getRealisationIdFromUrl() {
        const url = new URL(window.location.href);
        return url.searchParams.get('realisation_id') || url.searchParams.get('realisation');
    }

    updateUrlWithRealisation(realisationIndex) {
        if (realisationIndex < 0 || realisationIndex >= this.realisationsData.length) return;

        const realisation = this.realisationsData[realisationIndex];
        const url = new URL(window.location.href);
        url.searchParams.delete('realisation');
        url.searchParams.set('realisation_id', realisation.id);
        window.history.pushState({}, '', url.toString());
    }

    removeRealisationFromUrl() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('realisation_id') && !url.searchParams.has('realisation')) return;
        url.searchParams.delete('realisation_id');
        url.searchParams.delete('realisation');
        window.history.pushState({}, '', url.toString());
    }

    getRealisationIndexFromUrl() {
        const id = this.getRealisationIdFromUrl();
        if (!id) return -1;

        return this.realisationsData.findIndex((item) => String(item.id) === String(id));
    }

    checkUrlForModal() {
        const index = this.getRealisationIndexFromUrl();
        if (index !== -1) {
            this.openModal(index);
        }
    }

    handlePopState() {
        const index = this.getRealisationIndexFromUrl();
        if (index !== -1) {
            this.openModal(index);
        } else if (this.modal && this.modal.classList.contains('is-open')) {
            this.closeModal();
        }
    }

    handleKeyboard(e) {
        if (!this.modal || !this.modal.classList.contains('is-open')) return;

        switch (e.key) {
            case 'Escape':
                this.closeModal();
                break;
            case 'ArrowLeft':
                if (this.swiper) {
                    this.swiper.slidePrev();
                }
                break;
            case 'ArrowRight':
                if (this.swiper) {
                    this.swiper.slideNext();
                }
                break;
        }
    }

    destroy() {
        this.pauseAllCardVideos(true);
        this.pauseAllMedia();
        this.resetAllMedia();
        this.mediaPlayers = [];

        if (this.posterObserver) {
            this.posterObserver.disconnect();
            this.posterObserver = null;
        }

        if (this.posterExtractor) {
            this.posterExtractor.remove();
            this.posterExtractor = null;
        }

        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
        if (this.keyboardHandler) {
            document.removeEventListener('keydown', this.keyboardHandler);
        }
        window.removeEventListener('popstate', () => this.handlePopState());
    }
}
